import { Sizes } from "components/forms/NumberSelector";
import { StepPage } from "features/subscription-process/navigation/Steps/StepPage";
import { NsKey } from "enums/NsKey";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import Loading from 'components/ui/Loader';
import { QuestionsGroup } from './components/ProfilingQuestionsGroup';
import { ChevronLeftIcon, EyeIcon, PenIcon } from "components/icons/Icons";
import { useEffect, useMemo } from "react";
import { useUnemploymentProfiling } from "./hooks/useUnemploymentProfiling";
import { ContextCode, SurveyName, useGenericSurvey } from "./hooks/useGenericSurvey";
import { ProfilingMode } from "./slices/types/enums";
import { GenericSurveyPreview } from "features/subscription-process/profiling-process/components/Survey/GenericSurveyPreview";
import { useUnemploymentRiskData } from "../contexts/selectors/selectors";
import { Button } from "components/ui/Button";

export const ProfilingProcess = () => {
  const { t } = useDynamicTranslation([NsKey.Common, NsKey.Profiling]);
  const { initSurvey } = useGenericSurvey()

  const {
    currentQuestionGroup,
    showForm,
    setShowForm,
    isLastStep,
    isFirstStep,
    isCurrentQuestionAnswered,
    answers,
    isLoading,
    questionsToShow,
    handleQuestionAnswer,
    handleNext,
    handlePrevious,
  } = useUnemploymentProfiling();

  const riskData = useUnemploymentRiskData()
  const surveyAnswers = riskData?.surveyAnswers

  useEffect(() => {
    initSurvey(ContextCode.Unemployment, SurveyName.Unemployment, ProfilingMode.AutoAdvance)
  }, [initSurvey]);


  const disabledNextButton = useMemo(() => {
    if (surveyAnswers && surveyAnswers.length > 0) {
      return false
    }

    if (!isLastStep || isLoading) {
      return true
    }
    return !isCurrentQuestionAnswered
  }, [isLastStep, isCurrentQuestionAnswered, isLoading, surveyAnswers])

  const renderSurvey = useMemo(() => {
    return <div className="relative">
      <QuestionsGroup
        questions={questionsToShow}
        answers={answers}
        onAnswer={handleQuestionAnswer}
      />
    </div>
  }, [answers, handleQuestionAnswer, questionsToShow])

  if (!currentQuestionGroup) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="text-center">
          <div className="text-lg text-gray-600">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <StepPage
      containerSize={Sizes['5xl']}
      nextConfig={{
        label: t('COMMON.NEXT_STEP'),
        onClick: () => handleNext(),
        disabled: disabledNextButton,
      }}
    >
      <div className="relative">
        {
          !isFirstStep && !showForm &&
          <button className="flex gap-4 absolute" onClick={handlePrevious}>
            <ChevronLeftIcon />
            {t("COMMON.BACK")}
          </button>
        }
        {
          surveyAnswers && surveyAnswers.length > 0 &&
          <div className="absolute right-4">
            <Button color="slate" round onClick={() => setShowForm(prev => !prev)}>
              {showForm &&
                <>
                  Modifier les reponses
                  <PenIcon className="text-black" />
                </>
              }
              {!showForm &&
                <>
                  Voir les reponses
                  <EyeIcon className="text-black" />
                </>
              }
            </Button>
          </div>
        }
        <p className="text-xl text-center mb-4 mt-2 pt-1">{t("SURVEYS.UNEMPLOYMENT")}</p>
        {
          showForm &&
          <GenericSurveyPreview surveyForm={answers as any} />
        }
        {
          !showForm &&
          <div className="flex flex-col gap-6 py-4">
            {renderSurvey}
            {isLoading && <Loading loading={isLoading} isFullPage />}
          </div>
        }
      </div>
    </StepPage>
  );
};
